import { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import Modal from "react-modal";

const OrderDetails = ({
  isOpen,
  onRequestClose,
  selectedOrderUser,
  selectedOrderData,
  selectedOrderItems,
  selectedOrderID,
}) => {
  // Initialize and update user info when the prop changes.
  const [userInfo, setUserInfo] = useState({
    firstName: selectedOrderUser?.first_name || "",
    lastName: selectedOrderUser?.last_name || "",
    email: selectedOrderUser?.email || "",
  });

  useEffect(() => {
    if (selectedOrderUser) {
      setUserInfo({
        firstName: selectedOrderUser.first_name,
        lastName: selectedOrderUser.last_name,
        email: selectedOrderUser.email,
      });
    }
  }, [selectedOrderUser]);

  // Initialize and update shipping info when the prop changes.
  const [shippingInfo, setShippingInfo] = useState({
    name: selectedOrderData?.shipping_user_name || "",
    email: selectedOrderData?.shipping_user_email || "",
    area: selectedOrderData?.shipping_user_area || "",
    city: selectedOrderData?.shipping_user_city || "",
    phone: selectedOrderData?.shipping_user_mobile || "",
  });

  useEffect(() => {
    if (selectedOrderData) {
      setShippingInfo({
        name: selectedOrderData.shipping_user_name,
        email: selectedOrderData.shipping_user_email,
        area: selectedOrderData.shipping_user_area,
        city: selectedOrderData.shipping_user_city,
        phone: selectedOrderData.shipping_user_mobile,
      });
    }
  }, [selectedOrderData]);

  // Initialize and update order summary when the prop changes.
  const [orderSummary, setOrderSummary] = useState({
    paymentStatus: selectedOrderData?.payment_status || "",
    totalAmount: "0.00",
    paymentMethod: selectedOrderData?.payment_method || "",
    discount: selectedOrderData?.discount || "0.00",
    transactionId: selectedOrderData?.transaction_id || "",
    shippingCost: selectedOrderData?.shipping_cost || "0.00",
    finalAmount: "0.00",
  });

  useEffect(() => {
    if (selectedOrderData) {
      setOrderSummary({
        paymentStatus: selectedOrderData.payment_status,
        totalAmount: selectedOrderData.total_amount || "0.00",
        paymentMethod: selectedOrderData.payment_method,
        discount: selectedOrderData.discount || "0.00",
        transactionId: selectedOrderData.transaction_id,
        shippingCost: selectedOrderData.shipping_cost || "0.00",
        finalAmount: selectedOrderData.final_amount || "0.00",
      });
    }
  }, [selectedOrderData]);

  // Set order items and update when the prop changes.
  const [orderItems, setOrderItems] = useState(selectedOrderItems || []);

  useEffect(() => {
    setOrderItems(selectedOrderItems || []);
  }, [selectedOrderItems]);

  // Recalculate totals when order items, discount, or shipping cost changes.
  useEffect(() => {
    const computedTotal = orderItems.reduce(
      (sum, item) => sum + item.price_customer * item.quantity,
      0
    );
    const discountValue = parseFloat(orderSummary.discount) || 0;
    const shippingCostValue = parseFloat(orderSummary.shippingCost) || 0;
    const computedFinal = computedTotal - discountValue + shippingCostValue;

    setOrderSummary((prev) => ({
      ...prev,
      totalAmount: computedTotal.toFixed(2),
      finalAmount: computedFinal.toFixed(2),
    }));
  }, [orderItems, orderSummary.discount, orderSummary.shippingCost]);

  // Handler functions to update state.
  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrderSummaryChange = (e) => {
    const { name, value } = e.target;
    setOrderSummary((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    setOrderItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity: Number(newQuantity) } : item
      )
    );
  };

  const handleColorChange = (itemId, newColor) => {
    setOrderItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, color: newColor } : item
      )
    );
  };

  // Pass updated info in the API.
  const handleSaveChanges = async () => {
    const payload = {
      user: userInfo,
      shipping: shippingInfo,
      orderSummary: orderSummary,
      orderAllItems: orderItems,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orders/single-order/${selectedOrderID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update order.");
      }
      const data = await response.json();
      console.log("Order updated successfully:", data);
      // Optionally show a success message or perform further actions here.
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Order Details"
      className="text-black relative z-50 mx-auto max-w-screen-lg overflow-y-auto rounded-lg bg-white p-8 shadow-2xl"
      overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      style={{
        content: {
          maxHeight: "90vh",
          width: "90%",
          maxWidth: "800px",
        },
      }}
    >
      <div>
        {selectedOrderUser && (
          <div className="space-y-6">
            <button
              onClick={onRequestClose}
              className="data-html2canvas-ignore absolute right-6 top-12 p-2 text-gray-500 transition-colors duration-200 hover:text-gray-700"
              aria-label="Close"
            >
              <MdClose className="text-2xl" />
            </button>

            <h2 className="mb-6 text-3xl font-bold text-gray-900">
              Order Details
            </h2>

            <div className="rounded-xl bg-gray-50 p-6">
              <h3 className="mb-4 text-xl font-semibold text-gray-800">
                User Information
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-gray-600">Account Name</p>
                  <input
                    type="text"
                    name="firstName"
                    value={userInfo.firstName}
                    onChange={handleUserInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={userInfo.lastName}
                    onChange={handleUserInfoChange}
                    className="mt-2 w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Email</p>
                  <input
                    type="email"
                    name="email"
                    value={userInfo.email}
                    onChange={handleUserInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 p-6">
              <h3 className="mb-4 text-xl font-semibold text-gray-800">
                Shipping Information
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <input
                    type="text"
                    name="name"
                    value={shippingInfo.name}
                    onChange={handleShippingInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <input
                    type="email"
                    name="email"
                    value={shippingInfo.email}
                    onChange={handleShippingInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Area</p>
                  <input
                    type="text"
                    name="area"
                    value={shippingInfo.area}
                    onChange={handleShippingInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">City</p>
                  <input
                    type="text"
                    name="city"
                    value={shippingInfo.city}
                    onChange={handleShippingInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <input
                    type="text"
                    name="phone"
                    value={shippingInfo.phone}
                    onChange={handleShippingInfoChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 p-6">
              <h3 className="mb-4 text-xl font-semibold text-gray-800">
                Ordered Products
              </h3>
              {orderItems && orderItems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Product Name
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Color
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          SKU
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Price (Customer)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b transition-colors duration-200 hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <input
                              type="text"
                              name="color"
                              value={item.color}
                              onChange={(e) =>
                                handleColorChange(item.id, e.target.value)
                              }
                              className="w-full rounded border border-gray-300 p-2"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.sku}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <input
                              type="number"
                              name="quantity"
                              value={item.quantity}
                              onChange={(e) =>
                                handleQuantityChange(item.id, e.target.value)
                              }
                              className="w-full rounded border border-gray-300 p-2"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {(item.price_customer * item.quantity).toFixed(2)}{" "}
                            TK
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-600">No ordered products found.</p>
              )}
            </div>

            <div className="rounded-xl bg-gray-50 p-6">
              <h3 className="mb-4 text-xl font-semibold text-gray-800">
                Order Summary
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-gray-600">Payment Status</p>
                  <input
                    type="text"
                    name="paymentStatus"
                    value={orderSummary.paymentStatus}
                    readOnly
                    onChange={handleOrderSummaryChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <input
                    type="text"
                    name="totalAmount"
                    value={orderSummary.totalAmount}
                    readOnly
                    className="w-full rounded border border-gray-300 bg-gray-100 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <input
                    type="text"
                    name="paymentMethod"
                    value={orderSummary.paymentMethod}
                    onChange={handleOrderSummaryChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Discount</p>
                  <input
                    type="text"
                    name="discount"
                    value={orderSummary.discount}
                    onChange={handleOrderSummaryChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Transaction ID</p>
                  <input
                    type="text"
                    name="transactionId"
                    value={orderSummary.transactionId}
                    readOnly
                    onChange={handleOrderSummaryChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Shipping Cost</p>
                  <input
                    type="text"
                    name="shippingCost"
                    value={orderSummary.shippingCost}
                    onChange={handleOrderSummaryChange}
                    className="w-full rounded border border-gray-300 p-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Final Amount</p>
                  <input
                    type="text"
                    name="finalAmount"
                    value={orderSummary.finalAmount}
                    readOnly
                    className="w-full rounded border border-gray-300 bg-gray-100 p-2"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveChanges}
                className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OrderDetails;
